import { createRef } from "react"

import { ValueView } from "web/components"
import { isEmptyArray, isObjectWithProperty } from "web/services/helpers"

function columnsWithFrozenPositions(
  columns,
  frozenColumnCount,
  hidingDetails,
  canSelectRow,
  showRowNumbers
) {
  let columnLeftPosition = 0

  if (showRowNumbers && !hidingDetails) {
    columnLeftPosition += 30
  }

  if (canSelectRow && !hidingDetails) {
    columnLeftPosition += 30
  }

  var dataColumnCount = 0

  columns.forEach((column) => {
    column.leftPosition = columnLeftPosition

    if (column.type === "data") {
      dataColumnCount++

      column.freeze = dataColumnCount <= frozenColumnCount
      column.isLastFrozenColumn = dataColumnCount === frozenColumnCount
    } else {
      column.freeze = !!frozenColumnCount
      column.isLastFrozenColumn = false
    }

    columnLeftPosition += column.width
  })

  return columns
}

export function getColumnsToDisplay({
  columns,
  visibleColumnCount,
  groupColumn,
  secondGroupColumn,
  hasSecondGroup,
  hidingDetails,
  canSelectRow,
  showRowNumbers,
}) {
  const columnsToDisplay = columns.filter(
    (el) => el.dataKey !== groupColumn && el.dataKey !== secondGroupColumn
  )

  if (hidingDetails) {
    if (hasSecondGroup) {
      columnsToDisplay.unshift(
        columns.find((el) => el.dataKey === secondGroupColumn)
      )
    }

    columnsToDisplay.unshift(columns.find((el) => el.dataKey === groupColumn))
  }

  const requestedFrozenColumnCount =
    columnsToDisplay
      .filter((c) => c.type === "data")
      .findIndex((el) => el.isFrozen) + 1

  const frozenColumnCount = Math.min(
    visibleColumnCount,
    requestedFrozenColumnCount
  )

  return {
    frozenColumnCount,
    columnsToDisplay: columnsWithFrozenPositions(
      columnsToDisplay,
      frozenColumnCount,
      hidingDetails,
      canSelectRow,
      showRowNumbers
    ),
  }
}

export function getGridTemplateColumns({
  columns,
  canSelectRow,
  showRowNumbers,
}) {
  const gridColumns = []

  if (showRowNumbers) gridColumns.push("auto")

  if (canSelectRow) gridColumns.push("auto")

  const colWidth = (col) => `${col.width}px`

  gridColumns.push(
    ...columns
      .slice(0, -1)
      .map((col) => (col.type === "component" ? "auto" : colWidth(col)))
  )

  if (columns.length !== 0) {
    gridColumns.push(`minmax(${colWidth(columns[columns.length - 1])}, 1fr)`)
  }

  return gridColumns.join(" ")
}

export function getCells({
  rows,
  columns,
  tableIsEditable,
  currentUserIsDataAdmin,
}) {
  const cells = []
  let tabIndex = 1

  const getCellRefAndTabIndex = (col, row) => {
    const valueLocked =
      !!row.locked_fields &&
      Object.keys(row.locked_fields).includes(col.dataKey)

    const cellIsReadonly = row.readonlyFields?.includes(col.dataKey)

    const cellIsEditable =
      tableIsEditable &&
      row.isEditable &&
      col.isEditable &&
      !cellIsReadonly &&
      (!valueLocked || currentUserIsDataAdmin)

    return {
      ref: createRef(),
      dataKey: col.dataKey,
      isEditable: cellIsEditable,
      tabIndex: cellIsEditable ? tabIndex++ : undefined,
      valueLocked: valueLocked,
    }
  }

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]

    cells.push(columns.map((col) => getCellRefAndTabIndex(col, row)))
  }

  return cells
}

export function GroupDisplayValue({ value, columnLabel }) {
  const hasValidValue =
    isObjectWithProperty(value, "value") && !isEmptyArray(value.value)

  const valueToDisplay = hasValidValue
    ? value
    : { value: { value: `No ${columnLabel}` } }

  return (
    <ValueView
      {...valueToDisplay}
      has_more_info={false}
      percentValue={value?.percent_value}
    />
  )
}
